import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getApiV1AccesscontrolPermissionsSummaryQueryKey,
  usePostApiV1AccesscontrolPermissionsUpsertBatch,
} from '@/shared/api/generated';

import type { RolePermissionViewModel } from '@/shared/api/generated/types';

// ─── Tipos públicos ───────────────────────────────────────────────────────────

export interface SavePermissionsPayload {
  readonly roleId?: number;
  readonly permissonController?: RolePermissionViewModel[] | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook para salvar permissões em lote para um role.
 *
 * Encapsula `usePostApiV1AccesscontrolPermissionsUpsertBatch`. O endpoint recebe
 * um `roleId` e um array de `permissonController` e realiza upsert em lote.
 *
 * @returns `save` — função para salvar; `isPending` — estado de carregamento
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSavePermissions();
 * save({ roleId: 1, permissonController: batch });
 * ```
 */
export function useSavePermissions() {
  const queryClient = useQueryClient();
  const mutation = usePostApiV1AccesscontrolPermissionsUpsertBatch();
  const { t } = useTranslation('accessControl');

  const { alert } = useAlert();

  function save(payload: SavePermissionsPayload, callbacks?: { onSuccess?: () => void }) {
    const data = {
      ...(payload.roleId !== undefined && { roleId: payload.roleId }),
      permissonController: payload.permissonController ?? null,
    };

    mutation.mutate(
      { data },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getApiV1AccesscontrolPermissionsSummaryQueryKey(),
          });
          callbacks?.onSuccess?.();

          alert({
            alertType: 'success',
            title: t('permissions.alerts.saveSuccess.title'),
            description: t('permissions.alerts.saveSuccess.description'),
          });
        },
        onError: () => {
          alert({
            alertType: 'danger',
            title: t('permissions.alerts.saveError.title'),
            description: t('permissions.alerts.saveError.description'),
          });
        },
      },
    );
  }

  return { save, isPending: mutation.isPending, isError: mutation.isError } as const;
}
